import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { HeatGridWindow } from '@repo/frontend-types';

/**
 * Each person's heat settings on the onboarding camera pictures (`onboarding-heatmap-remembered`):
 * the Heatmap switch, the Observers layer, heat outlines and their threshold, and the heat window.
 *
 * One `localStorage` entry per signed-in person, the same on every twin and camera (R1). Read once
 * per person per tab, into one in-memory slot that every mounted surface shares, so a change on the
 * Observers step and on the full-screen picture is seen by both at once (R11). Another tab picks a
 * change up the next time it loads; nothing listens for `storage` events.
 *
 * The read and validation rules follow `observerWorkspace/cardPositions.ts`: every read and write is
 * inside try/catch, a field that does not validate takes its own default and nothing else, and an
 * entry of another version is never written over, so a newer build's settings survive an older
 * build in the same browser (R12, *Inputs and Missing Values*).
 */

export interface HeatSettings {
  heatOn: boolean;
  observersOn: boolean;
  outlinesOn: boolean;
  /** The outline threshold, a percentage: 5 to 95 in steps of 5, the slider's own bounds. */
  outlineEdge: number;
  window: HeatGridWindow;
}

/** R2. Also the values for any field never set, or stored but invalid. */
export const HEAT_SETTINGS_DEFAULTS: Readonly<HeatSettings> = Object.freeze({
  heatOn: true,
  observersOn: true,
  outlinesOn: false,
  outlineEdge: 30,
  window: '28d',
});

export const heatSettingsKey = (sub: string): string => `tl.onboarding.heatSettings.${sub}`;

const VERSION = 1;
const FIELDS: readonly (keyof HeatSettings)[] = [
  'heatOn',
  'observersOn',
  'outlinesOn',
  'outlineEdge',
  'window',
];
const WINDOWS: readonly HeatGridWindow[] = ['1d', '7d', '14d', '28d'];

const isEdge = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n >= 5 && n <= 95 && n % 5 === 0;
const isWindow = (w: unknown): w is HeatGridWindow => (WINDOWS as readonly unknown[]).includes(w);
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);

export interface HeatSettingsRead {
  values: HeatSettings;
  /** Every stored field this build does not know, kept and written back unchanged. */
  extras: Record<string, unknown>;
  /** Why the whole entry could not be read (storage threw, not JSON, another version), else null. */
  error: unknown;
  /** The entry is another version: read as the defaults and never written over. */
  foreign: boolean;
}

const EMPTY_READ: HeatSettingsRead = {
  values: HEAT_SETTINGS_DEFAULTS,
  extras: {},
  error: null,
  foreign: false,
};

export const readHeatSettings = (sub: string): HeatSettingsRead => {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(heatSettingsKey(sub));
  } catch (error) {
    return { ...EMPTY_READ, error };
  }
  if (raw === null) return EMPTY_READ;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return { ...EMPTY_READ, error };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ...EMPTY_READ, error: new Error('not a heat-settings entry') };
  }
  const entry = parsed as Record<string, unknown>;
  if (entry.v !== VERSION) {
    return {
      ...EMPTY_READ,
      // Never coerce the stored version: an object one (`{"toString":null}`) throws in `String`.
      error: new Error(
        `a ${typeof entry.v === 'number' ? `v${entry.v}` : typeof entry.v} heat-settings entry, not v${VERSION}`,
      ),
      foreign: true,
    };
  }
  const d = HEAT_SETTINGS_DEFAULTS;
  const values: HeatSettings = {
    heatOn: bool(entry.heatOn, d.heatOn),
    observersOn: bool(entry.observersOn, d.observersOn),
    outlinesOn: bool(entry.outlinesOn, d.outlinesOn),
    // Never clamped or rounded: a hand-edited 33 or 100 is the default, not a surprising neighbour.
    outlineEdge: isEdge(entry.outlineEdge) ? entry.outlineEdge : d.outlineEdge,
    window: isWindow(entry.window) ? entry.window : d.window,
  };
  const extras: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(entry)) {
    if (k !== 'v' && !(FIELDS as readonly string[]).includes(k)) extras[k] = v;
  }
  return { values, extras, error: null, foreign: false };
};

/* ── The tab's shared memory ─────────────────────────────────────────────────────────────────── */

interface Slot {
  /** `null` for nobody signed in: memory only, never read or written. */
  sub: string | null;
  values: HeatSettings;
  extras: Record<string, unknown>;
  foreign: boolean;
  listeners: Set<() => void>;
}

const slots = new Map<string, Slot>();
// Logged at most once per tab each, never naming the person (*Access and Data Exposure*).
let readWarned = false;
let writeWarned = false;

/**
 * What a warning may say about an error: its name only. A browser's storage error message can
 * quote the key, and the key holds the person's `sub` (Chromium's quota error does).
 */
const errorName = (error: unknown): string => (error instanceof Error ? error.name : typeof error);

const slotFor = (sub: string | null): Slot => {
  const key = sub ?? '';
  let slot = slots.get(key);
  if (!slot) {
    const read = sub ? readHeatSettings(sub) : EMPTY_READ;
    if (read.error && !readWarned) {
      readWarned = true;
      console.warn(
        '[heat-settings] could not read the stored settings, using defaults:',
        errorName(read.error),
      );
    }
    slot = {
      sub,
      values: read.values,
      extras: { ...read.extras },
      foreign: read.foreign,
      listeners: new Set(),
    };
    slots.set(key, slot);
  }
  return slot;
};

/** Whether the stored entry, as it is now, is another version's (another tab may have written it). */
const storedIsForeign = (sub: string): boolean => {
  const raw = window.localStorage.getItem(heatSettingsKey(sub));
  if (raw === null) return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return false; // not JSON: overwritten by the next change (*Inputs and Missing Values*)
  }
  return (
    !!parsed &&
    typeof parsed === 'object' &&
    'v' in parsed &&
    (parsed as { v: unknown }).v !== VERSION
  );
};

const write = (slot: Slot) => {
  if (!slot.sub || slot.foreign) return;
  try {
    // Checked again at the moment of writing, as card positions are: a newer build in another tab
    // may have written its own version since this tab read the entry, and that is never written over.
    if (storedIsForeign(slot.sub)) {
      slot.foreign = true;
      return;
    }
    window.localStorage.setItem(
      heatSettingsKey(slot.sub),
      JSON.stringify({ ...slot.extras, v: VERSION, ...slot.values }),
    );
  } catch (error) {
    // The choice still applies in memory for the session; a preference is not worth a message.
    if (!writeWarned) {
      writeWarned = true;
      console.warn('[heat-settings] could not save the settings:', errorName(error));
    }
  }
};

const change = <K extends keyof HeatSettings>(slot: Slot, field: K, value: HeatSettings[K]) => {
  if (slot.values[field] === value) return;
  // A new object, so `useSyncExternalStore` sees the change.
  slot.values = { ...slot.values, [field]: value };
  slot.listeners.forEach(listener => listener());
  write(slot);
};

/** Forgets every slot and both warnings — for tests, between cases. */
export const resetHeatSettingsMemory = (): void => {
  slots.clear();
  readWarned = false;
  writeWarned = false;
};

export interface HeatSettingsControls extends HeatSettings {
  setHeatOn: (on: boolean) => void;
  setObserversOn: (on: boolean) => void;
  setOutlinesOn: (on: boolean) => void;
  setOutlineEdge: (edge: number) => void;
  setWindow: (window: HeatGridWindow) => void;
}

/**
 * The signed-in person's heat settings and one setter per field. With no `sub` (absent or blank)
 * the defaults live in memory for the session and nothing is read or written.
 */
export const useHeatSettings = (sub: string | undefined): HeatSettingsControls => {
  const person = sub && sub.trim() ? sub : null;
  const subscribe = useCallback(
    (listener: () => void) => {
      const slot = slotFor(person);
      slot.listeners.add(listener);
      return () => {
        slot.listeners.delete(listener);
      };
    },
    [person],
  );
  const getSnapshot = useCallback(() => slotFor(person).values, [person]);
  const values = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // The setters keep their identity while the person does, so callers' callbacks do not churn.
  const setters = useMemo(() => {
    const set =
      <K extends keyof HeatSettings>(field: K) =>
      (value: HeatSettings[K]) =>
        change(slotFor(person), field, value);
    return {
      setHeatOn: set('heatOn'),
      setObserversOn: set('observersOn'),
      setOutlinesOn: set('outlinesOn'),
      setOutlineEdge: set('outlineEdge'),
      setWindow: set('window'),
    };
  }, [person]);
  return useMemo(() => ({ ...values, ...setters }), [values, setters]);
};
